import { FindingType } from './choices/finding-type';
import { Anatomy } from './choices/localized-findings/anatomy';
import { AssociatedSymptom } from './choices/localized-findings/associated-symptom';
import { Color } from './choices/localized-findings/color';
import { Decreased } from './choices/localized-findings/decreased';
import { Distribution } from './choices/localized-findings/distribution';
import { LesionConfig } from './choices/localized-findings/lesion-config';
import { LesionSurface } from './choices/localized-findings/lesion-surface';
import { LesionType } from './choices/localized-findings/lesion-type';
import { Onset } from './choices/localized-findings/onset';
import { Palpation } from './choices/localized-findings/palpation';
import { Pattern } from './choices/localized-findings/pattern';
import { Provoked } from './choices/localized-findings/provoked';
import { Quality } from './choices/localized-findings/quality';
import { Severity } from './choices/localized-findings/severity';
import { Sensitivity } from './choices/sensitivity';

/**
 * Common constructor properties for all localized findings.
 */
export interface LocalizedFindingConstructorOpts {
  id?: number;
  sensitivity: Sensitivity;
  radiates?: Anatomy[];
}

/**
 * Base class for a localized finding of either type pain, rash or swelling.
 */
export abstract class LocalizedFinding {
  public readonly type: FindingType;
  public readonly id: number | undefined;
  public sensitivity: Sensitivity;
  public radiates: Anatomy[];

  constructor(type: FindingType, opts: LocalizedFindingConstructorOpts) {
    this.type = type;
    this.id = opts.id != undefined ? opts.id : undefined;
    this.sensitivity = opts.sensitivity;
    this.radiates = opts.radiates || [];
  }

  abstract key(): string;

  isPain(): boolean {
    return this.type === FindingType.PAIN;
  }

  isRash(): boolean {
    return this.type === FindingType.RASH;
  }

  isSwelling(): boolean {
    return this.type === FindingType.SWELLING;
  }

  toString() {
    throw new Error(
      'toString() must be implemented in the subclasses of LocalizedFinding'
    );
  }

  abstract toJson(): any;

  static fromJson(json: any): LocalizedFinding {
    const findingType = FindingType.fromTag(json.finding);

    console.log('LocalizedFinding.fromJson: ', json, findingType);

    switch (findingType) {
      case FindingType.PAIN:
        return Pain.fromJson(json);
      case FindingType.RASH:
        return Rash.fromJson(json);
      case FindingType.SWELLING:
        return Swelling.fromJson(json);
    }

    throw new Error('Unknown FindingType');
  }
}

/**
 * Constructor properties for pain localized findings.
 */
export interface PainConstructorOpts extends LocalizedFindingConstructorOpts {
  onset?: Onset[];
  time?: Pattern[];
  provoked?: Provoked[];
  decreased?: Decreased[];
  quality?: Quality[];
  severity?: Severity[];
}

/**
 * A localized finding definition for pain.
 */
export class Pain extends LocalizedFinding {
  public onset: Onset[];
  public time: Pattern[];
  public provoked: Provoked[];
  public decreased: Decreased[];
  public quality: Quality[];
  public severity: Severity[];

  constructor(opts: PainConstructorOpts) {
    super(FindingType.PAIN, opts);
    this.onset = opts.onset || [];
    this.time = opts.time || [];
    this.provoked = opts.provoked || [];
    this.decreased = opts.decreased || [];
    this.quality = opts.quality || [];
    this.severity = opts.severity || [];
  }

  key(): string {
    const items = ['localized', 'pain'];
    for (const item of [
      'radiates',
      'onset',
      'time',
      'provoked',
      'decreased',
      'quality',
      'severity'
    ]) {
      items.push(item);
      items.push(
        (this as any)[item].map((i: any) => i.id ?? i.key ?? i.label).join(',')
      );
    }

    return items.join(':');
  }

  toJson(): any {
    return {
      id: this.id,
      finding: this.type.tag,
      sensitivity: this.sensitivity?.id,
      radiates: this.radiates.map((i) => i.id),
      onset: this.onset.map((i) => i.id),
      time: this.time.map((i) => i.id),
      provoked: this.provoked.map((i) => i.id),
      decreased: this.decreased.map((i) => i.id),
      quality: this.quality.map((i) => i.id),
      severity: this.severity.map((i) => i.id)
    };
  }

  static fromJson(json: any): Pain {
    const opts: PainConstructorOpts = {
      id: json.id,
      sensitivity: json.sensitivity
        ? Sensitivity.fromId(json.sensitivity)
        : Sensitivity.DEFAULT,
      radiates: json.radiates.map((r: any) => Anatomy.fromId(r)),
      onset: json.onset.map((r: any) => Onset.fromId(r)),
      time: json.time.map((r: any) => Pattern.fromId(r)),
      provoked: json.provoked.map((r: any) => Provoked.fromId(r)),
      decreased: json.decreased.map((r: any) => Decreased.fromId(r)),
      quality: json.quality.map((r: any) => Quality.fromId(r)),
      severity: json.severity.map((r: any) => Severity.fromId(r))
    };
    return new Pain(opts);
  }
}

/**
 * Constructor properties for rash localized findings.
 */
export interface RashConstructorOpts extends LocalizedFindingConstructorOpts {
  distribution?: Distribution[];
  lesionType?: LesionType[];
  lesionConfig?: LesionConfig[];
  lesionSurface?: LesionSurface[];
  color?: Color[];
  associatedSymptom?: AssociatedSymptom[];
  palpation?: Palpation[];
}

/**
 * A localized finding definition for rash.
 */
export class Rash extends LocalizedFinding {
  public distribution: Distribution[];
  public lesionType: LesionType[];
  public lesionConfig: LesionConfig[];
  public lesionSurface: LesionSurface[];
  public color: Color[];
  public associatedSymptom: AssociatedSymptom[];
  public palpation: Palpation[];

  constructor(opts: RashConstructorOpts) {
    super(FindingType.RASH, opts);
    this.distribution = opts.distribution || [];
    this.lesionType = opts.lesionType || [];
    this.lesionConfig = opts.lesionConfig || [];
    this.lesionSurface = opts.lesionSurface || [];
    this.color = opts.color || [];
    this.associatedSymptom = opts.associatedSymptom || [];
    this.palpation = opts.palpation || [];
  }

  key(): string {
    const items = ['localized', 'rash'];
    for (const item of [
      'radiates',
      'distribution',
      'lesionType',
      'lesionConfig',
      'lesionSurface',
      'associatedSymptom',
      'palpation'
    ]) {
      items.push(item);
      items.push(
        (this as any)[item].map((i: any) => i.id ?? i.key ?? i.label).join(',')
      );
    }

    return items.join(':');
  }

  toJson(): any {
    return {
      id: this.id,
      finding: this.type.tag,
      sensitivity: this.sensitivity.id,
      radiates: this.radiates.map((i) => i.id),
      distribution: this.distribution.map((i) => i.id),
      lesiontype: this.lesionType.map((i) => i.id),
      lesionconfig: this.lesionConfig.map((i) => i.id),
      lesionsurface: this.lesionSurface.map((i) => i.id),
      color: this.color.map((i) => i.id),
      associatedsymptom: this.associatedSymptom.map((i) => i.id),
      palpation: this.palpation.map((i) => i.id)
    };
  }

  static fromJson(json: any): Rash {
    const opts: RashConstructorOpts = {
      id: json.id,
      sensitivity: json.sensitivity
        ? Sensitivity.fromId(json.sensitivity)
        : Sensitivity.DEFAULT,
      radiates: json.radiates.map((r: any) => Anatomy.fromId(r)),
      distribution: json.distribution.map((r: any) => Distribution.fromId(r)),
      lesionType: json.lesiontype.map((r: any) => LesionType.fromId(r)),
      lesionConfig: json.lesionconfig.map((r: any) => LesionConfig.fromId(r)),
      lesionSurface: json.lesionsurface.map((r: any) => LesionSurface.fromId(r)),
      color: json.color.map((r: any) => Color.fromId(r)),
      palpation: json.palpation.map((r: any) => Palpation.fromId(r)),
      associatedSymptom: json.associatedsymptom.map((r: any) =>
        AssociatedSymptom.fromId(r)
      )
    };
    return new Rash(opts);
  }
}

/**
 * Constructor properties for swelling localized findings.
 */
export interface SwellingConstructorOpts extends LocalizedFindingConstructorOpts {
  onset?: Onset[];
  time?: Pattern[];
  provoked?: Provoked[];
  decreased?: Decreased[];
}

/**
 * A localized finding definition for swelling.
 */
export class Swelling extends LocalizedFinding {
  public onset: Onset[];
  public time: Pattern[];
  public provoked: Provoked[];
  public decreased: Decreased[];

  constructor(opts: SwellingConstructorOpts) {
    super(FindingType.SWELLING, opts);
    this.onset = opts.onset || [];
    this.time = opts.time || [];
    this.provoked = opts.provoked || [];
    this.decreased = opts.decreased || [];
  }

  key(): string {
    const items = ['localized', 'swelling'];
    for (const item of ['radiates', 'onset', 'time', 'provoked', 'decreased']) {
      items.push(item);
      items.push(
        (this as any)[item].map((i: any) => i.id ?? i.key ?? i.label).join(',')
      );
    }

    return items.join(':');
  }

  toJson(): any {
    return {
      id: this.id,
      finding: this.type.tag,
      sensitivity: this.sensitivity.id,
      radiates: this.radiates.map((i) => i.id),
      onset: this.onset.map((i) => i.id),
      time: this.time.map((i) => i.id),
      provoked: this.provoked.map((i) => i.id),
      decreased: this.decreased.map((i) => i.id)
    };
  }

  static fromJson(json: any): Swelling {
    const opts: SwellingConstructorOpts = {
      id: json.id,
      sensitivity: json.sensitivity
        ? Sensitivity.fromId(json.sensitivity)
        : Sensitivity.DEFAULT,
      radiates: json.radiates.map((r: any) => Anatomy.fromId(r)),
      onset: json.onset.map((r: any) => Onset.fromId(r)),
      time: json.time.map((r: any) => Pattern.fromId(r)),
      provoked: json.provoked.map((r: any) => Provoked.fromId(r)),
      decreased: json.decreased.map((r: any) => Decreased.fromId(r))
    };
    return new Swelling(opts);
  }
}
